import { useCallback } from 'react';
import { useFragment, useMutation } from 'react-relay';
import graphql from 'babel-plugin-relay/macro';

import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import StoredFile from 'components/StoredFile';

import { useErrorHandler } from 'lib';
import { OrderEditInvoice_order$key } from '__generated__/OrderEditInvoice_order.graphql';
import { OrderEditInvoiceUploadMutation } from '__generated__/OrderEditInvoiceUploadMutation.graphql';
import { OrderEditInvoiceDeleteMutation } from '__generated__/OrderEditInvoiceDeleteMutation.graphql';

type Props = {
  order: OrderEditInvoice_order$key;
};

const accept = ['image/jpeg', 'image/png', 'application/pdf'];

const OrderEditInvoice = (props: Props) => {
  const errorHandler = useErrorHandler();

  const { id, invoice } = useFragment(
    graphql`
      fragment OrderEditInvoice_order on Order {
        id

        invoice {
          ...StoredFile_file
        }
      }
    `,
    props.order
  );

  const [upload, isUploading] =
    useMutation<OrderEditInvoiceUploadMutation>(graphql`
      mutation OrderEditInvoiceUploadMutation($id: ID!, $file: Upload!) {
        uploadOrderInvoice(id: $id, file: $file) {
          invoice {
            ...StoredFile_file
          }
        }
      }
    `);

  const [deleteInvoice, isDeleting] =
    useMutation<OrderEditInvoiceDeleteMutation>(
      graphql`
        mutation OrderEditInvoiceDeleteMutation($id: ID!) {
          deleteOrderInvoice(id: $id) {
            invoice {
              ...StoredFile_file
            }
          }
        }
      `
    );

  const handleDrop = useCallback(
    (accepted: File[]) => {
      if (accepted.length < 1) return;

      upload({
        variables: { id, file: null },
        uploadables: { file: accepted[0] },
        ...errorHandler(),
      });
    },
    [errorHandler, id, upload]
  );

  const handleDelete = useCallback(() => {
    deleteInvoice({
      variables: { id },
      ...errorHandler(),
    });
  }, [deleteInvoice, errorHandler, id]);

  return (
    <Box sx={{ mt: 2 }}>
      <Typography>Sąskaitos dokumentas klientui</Typography>

      <StoredFile
        file={invoice}
        loading={isUploading || isDeleting}
        accept={accept}
        disabled={Boolean(invoice)}
        rejectMessage="Netinkamas failo formatas? (Galima įkelti tik jpg, png ir pdf documentus)"
        onDrop={handleDrop}
        onDelete={handleDelete}
      />
    </Box>
  );
};

export default OrderEditInvoice;
